import { PatternType } from '../constants-types/Enums';
import { Pattern } from '../core-data-model/Pattern.model';
import { ABCD, BehavioralFactorNoE } from './BehavioralFactor.model';
import { MinMax } from './Common.model';
import {
    ReferencePatternType,
    ReferencePatternsInformation
} from './ReferencePatternType.model';

type KeyFormatCallback = (factor: string) => string;

export type BehavioralFactorRangesInput = {
    A: MinMax;
    B: MinMax;
    C: MinMax;
    D: MinMax;
};

export class BehavioralFactorRanges {
    [key: string]: MinMax;
    A: MinMax;
    B: MinMax;
    C: MinMax;
    D: MinMax;

    constructor(obj?: BehavioralFactorRangesInput) {
        if (obj) {
            Object.assign(this, obj);
        }
    }

    static equals(one: BehavioralFactorRanges, two: BehavioralFactorRanges) {
        if (!one || !two) {
            return false;
        }
        const areEqual = (key: string) =>
            one[key].min === two[key].min && one[key].max === two[key].max;
        return (
            Object.keys(one).every(areEqual) && Object.keys(two).every(areEqual)
        );
    }

    static duplicateRanges(
        ranges: BehavioralFactorRanges
    ): BehavioralFactorRanges {
        const duplicate = new BehavioralFactorRanges();
        ABCD.forEach(
            (factor) =>
                (ranges[factor] = new MinMax({
                    min: ranges[factor].min,
                    max: ranges[factor].max
                }))
        );
        return duplicate;
    }

    // Extract the ranges based on a function that generates the keys for the
    // values stored in a given input object
    // Expects server input like: {
    //     aSigmaRangeLow: number;
    //     aSigmaRangeHigh: number;
    //     bSigmaRangeLow: number;
    //     etc...
    // }
    static extractRanges(
        ranges: any,
        cb: KeyFormatCallback
    ): BehavioralFactorRanges {
        return new BehavioralFactorRanges(
            ABCD.reduce((object, factor) => {
                object[factor] = new MinMax({
                    min: ranges[cb(factor) + 'Low'],
                    max: ranges[cb(factor) + 'High']
                });
                return object;
            }, {} as BehavioralFactorRangesInput)
        );
    }

    static isEmpty(ranges: BehavioralFactorRanges): boolean {
        return !ranges || ABCD.every((factor) => ranges[factor].delta === 0);
    }

    static patternToRanges(
        pattern: Pattern,
        width = 1
    ): BehavioralFactorRanges {
        const halfWidth = width / 2;
        const ranges = new BehavioralFactorRanges();
        ABCD.forEach((factor) => {
            // NOTE: This is where we enforce having the ranges not go outside
            // of our enforced 3 sigma bounds
            const sigmaValue = Math.max(Math.min(pattern[factor], 2.5), -2.5);
            ranges[factor] = new MinMax({
                min: sigmaValue - halfWidth,
                max: sigmaValue + halfWidth
            });
        });
        return ranges;
    }

    static rangesToPattern(ranges: BehavioralFactorRanges): Pattern {
        const pattern = new Pattern();
        ABCD.forEach((factor) => (pattern[factor] = ranges[factor].midpoint));
        return pattern;
    }

    /**
     * Right now this will take the min from all the patterns and the max from
     * all the patterns for each factor.
     *
     * NOTE: This will create ranges from the patterns with default width of 1.
     *
     * E.g.
     * pattern1: { A: 1, B: 1, C: 1, D: 1 }
     * pattern2: { A: -1, B: -1, C: -1, D:-1 }
     *
     * outputRanges = { A: { min: -1.5, max: 1.5}, B: { min: -1.5, max: 1.5 }, ... }
     */
    static combinePatternsIntoSingleRange(
        patterns: Pattern[]
    ): BehavioralFactorRanges {
        const ranges = new BehavioralFactorRanges();
        const rangesFromPatterns = patterns.map((p) => this.patternToRanges(p));
        ABCD.forEach((factor) => {
            // There's a more efficient way to do this but this is simple and easy
            // to read
            const min = rangesFromPatterns.reduce(
                (previous, current) =>
                    current[factor].min < previous
                        ? current[factor].min
                        : previous,
                Number.MAX_SAFE_INTEGER
            );
            const max = rangesFromPatterns.reduce(
                (previous, current) =>
                    current[factor].max > previous
                        ? current[factor].max
                        : previous,
                Number.MIN_SAFE_INTEGER
            );
            ranges[factor] = new MinMax({
                min,
                max
            });
        });
        return ranges;
    }

    static referenceProfileToRanges(
        referenceProfile: ReferencePatternType
    ): BehavioralFactorRanges {
        const patternFromReferenceProfile = new Pattern({
            A: referenceProfile.rangeMidpointValues.A,
            B: referenceProfile.rangeMidpointValues.B,
            C: referenceProfile.rangeMidpointValues.C,
            D: referenceProfile.rangeMidpointValues.D
        });
        return this.patternToRanges(patternFromReferenceProfile);
    }

    static rangeOverlapForFactor(
        factor: BehavioralFactorNoE,
        rangesA: BehavioralFactorRanges,
        rangesB: BehavioralFactorRanges
    ): number {
        const A = rangesA[factor];
        const B = rangesB[factor];
        return Math.max(0, Math.min(A.max, B.max) - Math.max(A.min, B.min));
    }

    static rangesMatchScore(
        rangesA: BehavioralFactorRanges,
        rangesB: BehavioralFactorRanges
    ): number {
        return ABCD.reduce(
            (sum, factor) =>
                sum + this.rangeOverlapForFactor(factor, rangesA, rangesB),
            0
        );
    }

    // This is intended to match code in Server - DetermineReferencePattern.FindStandardizedReferencePattern
    // really business logic should NOT be in the front end, and definitely should not be duplicated between front
    // and back end where it can quickly become out of sync resulting in conflicting UI displays.
    static closestReferenceProfile(pattern: Pattern): ReferencePatternType {
        const AdapterPatternValue = 0.4;
        const MaxStandardizedSigmaValue = 7;
        const maxSigma = Math.max(
            Math.max(
                Math.max(Math.abs(pattern.A), Math.abs(pattern.B)),
                Math.abs(pattern.C)
            ),
            Math.abs(pattern.D)
        );
        let patternType: PatternType;

        if (
            Math.abs(pattern.A) <= AdapterPatternValue &&
            Math.abs(pattern.B) <= AdapterPatternValue &&
            Math.abs(pattern.C) <= AdapterPatternValue &&
            Math.abs(pattern.D) <= AdapterPatternValue
        ) {
            patternType = PatternType.Adapter;
        } else {
            const standardizedSigmaA =
                (pattern.A * MaxStandardizedSigmaValue) / maxSigma;
            const standardizedSigmaB =
                (pattern.B * MaxStandardizedSigmaValue) / maxSigma;
            const standardizedSigmaC =
                (pattern.C * MaxStandardizedSigmaValue) / maxSigma;
            const standardizedSigmaD =
                (pattern.D * MaxStandardizedSigmaValue) / maxSigma;

            let minDist = Number.MAX_SAFE_INTEGER;

            const distance = (
                a: number,
                b: number,
                c: number,
                d: number,
                sa: number,
                sb: number,
                sc: number,
                sd: number
            ) =>
                Math.sqrt(
                    (a - sa) * (a - sa) +
                        (b - sb) * (b - sb) +
                        (c - sc) * (c - sc) +
                        (d - sd) * (d - sd)
                );

            ReferencePatternsInformation.filter(
                (profile: ReferencePatternType) => profile.id !== patternType
            ).forEach((profile: ReferencePatternType) => {
                const dist = distance(
                    standardizedSigmaA,
                    standardizedSigmaB,
                    standardizedSigmaC,
                    standardizedSigmaD,
                    profile.standardizedRanges.A,
                    profile.standardizedRanges.B,
                    profile.standardizedRanges.C,
                    profile.standardizedRanges.D
                );
                if (minDist > dist) {
                    patternType = profile.id;
                    minDist = dist;
                }
            });
        }

        return ReferencePatternsInformation.find(
            (profile) => profile.id === patternType
        );
    }
}
